import { useMemo } from "react";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import DatafieldSection from "../../../components/datafield-section";
import {
  DrawdownLoan,
  RetailODDetails,
  RetailOdLedgerInfoDto,
  TermLoanDetails,
  TermLoanLedgerInfoDto
} from "../../../dto";
import { FieldLabel, LoanStatusEnum } from "../../../models";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../../utils";
import { refactorLoanData } from "../../loan-details/helpers";
import { ExtendedLoanType } from "../types";

interface LoanSummaryProps {
  errorMessage?: string;
  loanDetails?: TermLoanDetails | RetailODDetails | null;
  termLoanLedgerInfo?: TermLoanLedgerInfoDto | null;
  retailOdLedgerInfo?: RetailOdLedgerInfoDto | null;
  drawdownLoanDetails?: DrawdownLoan | null;
  isDrawdownLoan?: boolean;
  onWriteOffBtnClick?: () => void;
  onCloseCreditLineBtnClick?: () => void;
}

export default function LoanSummary({
  loanDetails,
  termLoanLedgerInfo,
  retailOdLedgerInfo,
  drawdownLoanDetails,
  isDrawdownLoan,
  onWriteOffBtnClick,
  onCloseCreditLineBtnClick,
  errorMessage,
}: LoanSummaryProps) {
  const LOAN_TYPE_TO_LEFT_LABEL_MAP: Record<ExtendedLoanType, FieldLabel[]> = {
    TERM_LOAN: [
      {
        label: "Sanction Amount",
        key: "approvedAmount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      { label: "Tenor (Months)", key: "tenureInMonths" },
      { label: "Loan Type", key: "loanType", formatMethod: splitKeyWithSpace },
      {
        label: "Source",
        key: "clientId",
      },
    ],
    RETAIL_OD: [
      {
        label: "Sanction Amount",
        key: "maxAmount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      { label: "Valid Upto", key: "expiredAt", formatMethod: dateToDDMMYYYY },
    ],
    RETAIL_OD_DRAWDOWN: [
      {
        label: "Amount",
        key: "amount",
        prefix: "Rs ",
        formatMethod: (value: number) => value.toFixed(2),
      },
      { label: "Tenor (Months)", key: "tenureInMonths" },
      {
        label: "Processing Fee (with GST)",
        key: "pfPostGst",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
    ],
  };

  const LOAN_TYPE_TO_RIGHT_LABEL_MAP: Record<ExtendedLoanType, FieldLabel[]> = {
    TERM_LOAN: [
      {
        label: "Processing Fee (with GST)",
        key: "pfPostGst",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      {
        label: "Insurance (with GST)",
        key: "insuranceWithGst",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      { label: "Interest Rate (PA)", key: "irpa", suffix: "%" },
      {
        label: "Interest Rate Type",
        key: "interestRateType",
        formatMethod: splitKeyWithSpace,
      },
    ],
    RETAIL_OD: [
      { label: "Loan Type", key: "loanType", formatMethod: splitKeyWithSpace },
      {
        label: "Source",
        key: "clientId",
      },
    ],
    RETAIL_OD_DRAWDOWN: [
      { label: "Interest Rate (PA)", key: "irpa", suffix: "%" },
      {
        label: "Interest Rate Type",
        key: "interestRateType",
        formatMethod: splitKeyWithSpace,
      },
    ],
  };

  const LOAN_TYPE_HEADING_MAP: Record<ExtendedLoanType, string> = {
    TERM_LOAN: "Terms",
    RETAIL_OD: "Loan Details",
    RETAIL_OD_DRAWDOWN: "Drawdown",
  };

  const loanType: ExtendedLoanType = isDrawdownLoan
    ? "RETAIL_OD_DRAWDOWN"
    : (loanDetails?.loanType as ExtendedLoanType);

  const data = isDrawdownLoan
    ? drawdownLoanDetails
    : refactorLoanData(loanDetails, termLoanLedgerInfo, retailOdLedgerInfo);

  const labelsLeft = useMemo(() => {
    return LOAN_TYPE_TO_LEFT_LABEL_MAP[loanType];
  }, [loanType]);

  const labelsRight = useMemo(() => {
    return LOAN_TYPE_TO_RIGHT_LABEL_MAP[loanType];
  }, [loanType]);

  if (!data || !labelsLeft || !labelsRight) return null;

  if( data && 'loanType' in data && data.loanType === "RETAIL_OD"){
      data.loanType = "Earned Wage Access"
    }

  return (
    <>
      <Alert type="danger" message={errorMessage} canDismiss />

      <DatafieldSection
        title={LOAN_TYPE_HEADING_MAP[loanType]}
        labels={[{ items: labelsLeft }, { items: labelsRight }]}
        data={data}
      />
    </>
  );
}
